import type { Business } from '@/modules/dashboard/dashboardTypes';
import { Checkmark } from '@/resources/assets/images';
import Loader, { Status } from '@/components/Loader/Loader';
import { Dialog } from '@/components/atoms/Dialog';
import { ChangeEvent, useRef, useState } from 'react';

import { Input } from '@/components/atoms/Input';
import Button from '@/components/Button/newButtons';
import type { User } from '@/modules/user/userTypes';
import type { DashboardAddBusinessDataProps } from '@/modules/dashboard/dashboardActions';

export type AddContactDataModalProps = {
	businessUserAddedData: Business['userAddedData'];
	businessName: Business['name'];
	businesslistId: Business['listId'];
	businessId: Business['_id'];
	isOpen: boolean;
	onCloseModal: VoidFunction;
	userEmail: User['email'];
	onAddContactData: (props: DashboardAddBusinessDataProps) => void;
	isContactDataLoading: boolean;
	isContactDataError: boolean;
	isContactDataSuccess: boolean;
};

type ContactGenerateProps = {
	label: string;
	placeholder: string;
	error: {
		empty: string;
		duplicated: string;
		invalidFormat?: string;
	};
	linkError?: {
		empty: string;
		duplicated: string;
		invalidFormat: string;
	};
};

const contactGenerateTypeOptions: Record<
	DashboardAddBusinessDataProps['type'],
	ContactGenerateProps
> = {
	'web-link': {
		label: 'Web Link',
		placeholder: 'eg. Customer Website',
		error: {
			empty: 'Web link must not be empty',
			duplicated: 'This link name is already being used',
			invalidFormat: ''
		},
		linkError: {
			empty: 'Link must not be empty',
			duplicated: 'This link already exists',
			invalidFormat: 'Please format website link correctly'
		}
	},
	email: {
		label: 'Email',
		placeholder: 'eg. manager@company.com',
		error: {
			empty: 'Email must not be empty',
			duplicated: 'This email already exists',
			invalidFormat: 'Email incorrectly formatted'
		}
	},
	phone: {
		label: 'Phone',
		placeholder: 'eg. (555)-638-2751',
		error: {
			empty: 'Phone must not be empty',
			duplicated: 'This phone number already exists'
		}
	}
} as const;

const formatGenerateValue = (value: string) => {
	return value
		?.trim()
		.replace(/[()-.\s]/gim, '')
		.replace('www', '')
		.replace('http://', '')
		.replace('https://', '')
		.trim();
};

const checkUrlValue = (linkValue: string) => {
	const urlReg = new RegExp(
		/^((https?|ftp|smtp):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/
	);

	return urlReg.test(linkValue);
};

const checkEmailValue = (linkValue: string) => {
	const emailRegExp = new RegExp(
		/^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/
	);

	return emailRegExp.test(linkValue);
};

export const AddContactDataModal = ({
	businessUserAddedData,
	businessName,
	businesslistId,
	businessId,
	isOpen,
	onCloseModal,
	userEmail,
	onAddContactData,
	isContactDataLoading,
	isContactDataError,
	isContactDataSuccess
}: AddContactDataModalProps) => {
	const mainInputRef = useRef<HTMLInputElement>(null);
	const linkInputRef = useRef<HTMLInputElement>(null);

	const [contactGenerateType, setContactGenerateType] =
		useState<DashboardAddBusinessDataProps['type']>('web-link');
	const [errormessage, setErrorMessage] = useState('');

	const handleUpdateContactGenerateType = (
		e: ChangeEvent<HTMLInputElement>
	) => {
		setErrorMessage('');

		setContactGenerateType(
			e.target.value as DashboardAddBusinessDataProps['type']
		);
	};

	const isWebLink = contactGenerateType === 'web-link';
	const isEmail = contactGenerateType === 'email';

	const handleSetError = (errorMessage: string) => {
		setErrorMessage(errorMessage);

		setTimeout(() => {
			setErrorMessage('');
		}, 2.5 * 1000);

		return null;
	};

	const handleHasErrors = () => {
		const hasMainValue = mainInputRef.current?.value;
		const hasLinkValue = linkInputRef.current?.value;

		if (!hasMainValue) {
			handleSetError(
				contactGenerateTypeOptions[contactGenerateType].error.empty
			);

			return true;
		}

		const mainValueAlreadyExist = Boolean(
			businessUserAddedData?.find(
				({ data }) =>
					formatGenerateValue(data) ===
					formatGenerateValue(mainInputRef.current?.value || '')
			)
		);

		if (mainValueAlreadyExist) {
			handleSetError(
				contactGenerateTypeOptions[contactGenerateType].error.duplicated
			);

			return true;
		}

		if (isWebLink) {
			if (!hasLinkValue) {
				handleSetError(
					contactGenerateTypeOptions['web-link'].linkError?.empty ||
						''
				);

				return true;
			}

			const linkValueAlreadyExist = Boolean(
				businessUserAddedData?.find(
					({ data }) =>
						formatGenerateValue(data) ===
						formatGenerateValue(linkInputRef.current?.value || '')
				)
			);

			if (linkValueAlreadyExist) {
				handleSetError(
					contactGenerateTypeOptions['web-link'].linkError
						?.duplicated || ''
				);

				return true;
			}

			const isUrlValid = checkUrlValue(linkInputRef.current?.value);

			if (!isUrlValid) {
				handleSetError(
					contactGenerateTypeOptions['web-link'].linkError
						?.invalidFormat || ''
				);

				return true;
			}
		}

		if (isEmail) {
			const isEmailValid = checkEmailValue(mainInputRef.current?.value);

			if (!isEmailValid) {
				handleSetError(
					contactGenerateTypeOptions['email'].error.invalidFormat ||
						''
				);

				return true;
			}
		}

		return false;
	};

	const handleSubmitContactData = () => {
		const hasError = handleHasErrors();

		if (!hasError && mainInputRef.current?.value) {
			onAddContactData({
				type: contactGenerateType,
				businessId,
				userEmail,
				mainData: mainInputRef.current?.value,
				linkData: linkInputRef.current?.value,
				listId: businesslistId
			});

			onCloseModal();
		}
	};

	return (
		<Dialog.Root open={isOpen} onOpenChange={onCloseModal}>
			<Dialog.Content className="flex flex-col items-center justify-center gap-4">
				<Dialog.Title className="text-2xl text-dm-white">
					Add Contact Data to{' '}
					<span className="text-orange">{businessName}</span>
				</Dialog.Title>

				<div className="flex w-full max-w-xs flex-col gap-8 pt-11">
					<section className="flex flex-col gap-3">
						<header className="text-sm text-dm-white">
							What type of data would you like to add?
						</header>

						<div className="flex w-full items-center justify-between gap-2">
							{Object.entries(contactGenerateTypeOptions).map(
								([option, value]) => (
									<label
										key={option}
										className="group/contact-button w-full cursor-pointer"
									>
										<input
											type="radio"
											className="peer"
											name="contact-type"
											value={option}
											checked={
												contactGenerateType === option
											}
											onChange={
												handleUpdateContactGenerateType
											}
											hidden
										/>
										<span className="flex h-12 w-full items-center justify-center rounded-md bg-dm-black4 text-light-grey transition-colors group-hover/contact-button:bg-dm-dark-gold group-hover/contact-button:text-dm-gold peer-checked:bg-dm-dark-gold peer-checked:text-dm-gold">
											{value.label}
										</span>
									</label>
								)
							)}
						</div>
					</section>

					<section className="flex flex-col gap-4">
						{errormessage && (
							<p className="text-xs text-light-red">
								{errormessage}
							</p>
						)}

						<Input
							placeholder={
								contactGenerateTypeOptions[contactGenerateType]
									.placeholder
							}
							size="lg"
							ref={mainInputRef}
						/>

						{isWebLink && (
							<Input
								placeholder="eg. www.examplewebsite.com"
								ref={linkInputRef}
								size="lg"
							/>
						)}
					</section>

					<Button
						onClick={handleSubmitContactData}
						size="large"
						fullWidth
						className="relative h-12 text-base"
					>
						Add Data
						<span className="absolute -right-4 top-1/2 flex -translate-y-1/2 translate-x-full items-center justify-center">
							{isContactDataLoading && (
								<Loader status={Status.LOADING} />
							)}

							{isContactDataError && (
								<span className="failure-mark">X</span>
							)}

							{isContactDataSuccess && (
								<Checkmark className="reminder-checkmark" />
							)}
						</span>
					</Button>
				</div>
			</Dialog.Content>
		</Dialog.Root>
	);
};
